import { MESSAGE_TYPE } from 'shared/constants/messages';
import { showBadgeOnFavicon } from './faviconHelper';
import { initFaviconSwitcher } from './faviconHelper';

import { EVENT_TYPES } from 'dashboard/routes/dashboard/settings/profile/constants.js';
import GlobalStore from 'dashboard/store';
import AudioNotificationStore from './AudioNotificationStore';
import {
  isConversationAssignedToMe,
  isConversationUnassigned,
  isMessageFromCurrentUser,
} from './AudioMessageHelper';
import WindowVisibilityHelper from './WindowVisibilityHelper';
import { useAlert } from 'dashboard/composables';
import { installRingerUnlock, startRing, stopRing } from './ringer';
import { DEFAULT_AUDIO_ALERT_TYPE } from './alertDefaults';

const NOTIFICATION_TIME = 30000;
const ALERT_DURATION = 10000;
const ALERT_PATH_PREFIX = '/audio/dashboard/';
const DEFAULT_TONE = 'ding';
const RING_CHECK_INTERVAL = 1000;
// While ringing, phones that can vibrate do so every few seconds, and the
// tab title flashes so a ringing tab stands out among the others.
const VIBRATE_EVERY_TICKS = 3;
const VIBRATION_PATTERN = [700, 300, 700];
const RING_TITLE = '🔔 New chat waiting';
const DEFAULT_ALERT_TYPE = DEFAULT_AUDIO_ALERT_TYPE.split('+');
// A visitor arriving gets its own sound, so it isn't mistaken for a chat.
const VISITOR_TONE_URL = `${ALERT_PATH_PREFIX}bell.mp3`;
const VISITOR_TOAST_DURATION = 6000;

export class DashboardAudioNotificationHelper {
  constructor(store) {
    if (!store) {
      throw new Error('store is required');
    }
    this.store = new AudioNotificationStore(store);

    this.notificationConfig = {
      audioAlertType: DEFAULT_ALERT_TYPE,
      playAlertOnlyWhenHidden: true,
      alertIfUnreadConversationExist: false,
      ringUntilOpened: false,
    };

    this.recurringNotificationTimer = null;

    // Conversations ringing until the agent opens them
    // (id -> { wasLoaded: seen in the conversation list yet, newChat }).
    this.ringingConversations = new Map();
    this.ringWatcher = null;
    this.ringTicks = 0;
    this.titleBeforeRing = null;

    this.audioConfig = {
      audio: null,
      tone: DEFAULT_TONE,
      hasSentSoundPermissionsRequest: false,
    };

    this.currentUser = null;
  }

  intializeAudio = () => {
    const resourceUrl = `${ALERT_PATH_PREFIX}${this.audioConfig.tone}.mp3`;
    this.audioConfig.audio = new Audio(resourceUrl);
    return this.audioConfig.audio.load();
  };

  playAudioAlert = async () => {
    try {
      await this.audioConfig.audio.play();
    } catch (error) {
      this.handlePlayError(error);
    }
  };

  handlePlayError = error => {
    if (
      error.name === 'NotAllowedError' &&
      !this.hasSentSoundPermissionsRequest
    ) {
      this.hasSentSoundPermissionsRequest = true;
      useAlert(
        'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.SOUND_PERMISSION_ERROR',
        { usei18n: true, duration: ALERT_DURATION }
      );
    }
  };

  // ---------- Ring until opened (TalkRobin) ----------

  startRinging = (conversationId, { newChat = false } = {}) => {
    const ringing = this.ringingConversations.get(conversationId);
    if (!ringing) {
      this.ringingConversations.set(conversationId, {
        wasLoaded: false,
        newChat,
      });
    } else if (newChat) {
      ringing.newChat = true;
    }
    startRing().catch(this.handlePlayError);
    if (!this.ringWatcher) {
      this.ringTicks = 0;
      this.titleBeforeRing = document.title;
      this.ringWatcher = setInterval(this.checkRinging, RING_CHECK_INTERVAL);
    }
  };

  // A conversation stops ringing once the agent has it open on screen, when
  // it's resolved or snoozed, or when it disappears from the list (deleted).
  // A new chat also stops once a teammate has picked it up and replied.
  checkRinging = () => {
    this.ringingConversations.forEach((ringing, conversationId) => {
      const isLoaded = this.store.isConversationLoaded(conversationId);
      const answered =
        WindowVisibilityHelper.isWindowVisible() &&
        this.store.isConversationSelected(conversationId);
      const deleted = ringing.wasLoaded && !isLoaded;
      const closed = this.store.isConversationClosed(conversationId);
      const takenByTeammate =
        ringing.newChat &&
        this.store.isAnsweredByAnotherAgent(
          conversationId,
          this.currentUser?.id
        );
      if (answered || deleted || closed || takenByTeammate) {
        this.ringingConversations.delete(conversationId);
      } else if (isLoaded) {
        ringing.wasLoaded = true;
      }
    });
    if (!this.ringingConversations.size) {
      this.stopRinging();
      return;
    }
    this.ringTicks += 1;
    // Sound can be blocked until the first tap; keep trying meanwhile.
    startRing().catch(() => {});
    if (this.ringTicks % VIBRATE_EVERY_TICKS === 1) {
      navigator.vibrate?.(VIBRATION_PATTERN);
    }
    document.title =
      this.ringTicks % 2 ? RING_TITLE : this.titleBeforeRing || RING_TITLE;
  };

  stopRinging = () => {
    this.ringingConversations.clear();
    if (this.ringWatcher) {
      clearInterval(this.ringWatcher);
      this.ringWatcher = null;
    }
    stopRing();
    navigator.vibrate?.(0);
    if (this.titleBeforeRing !== null) {
      document.title = this.titleBeforeRing;
      this.titleBeforeRing = null;
    }
  };

  set = ({
    currentUser,
    alwaysPlayAudioAlert,
    alertIfUnreadConversationExist,
    ringUntilOpened = true,
    audioAlertType = DEFAULT_AUDIO_ALERT_TYPE,
    audioAlertTone = DEFAULT_TONE,
  }) => {
    this.notificationConfig = {
      ...this.notificationConfig,
      audioAlertType: audioAlertType.split('+').filter(Boolean),
      playAlertOnlyWhenHidden: !alwaysPlayAudioAlert,
      alertIfUnreadConversationExist: alertIfUnreadConversationExist,
      ringUntilOpened,
    };
    if (ringUntilOpened) installRingerUnlock();
    else this.stopRinging();

    this.currentUser = currentUser;

    const previousAudioTone = this.audioConfig.tone;
    this.audioConfig = {
      ...this.audioConfig,
      tone: audioAlertTone,
    };

    if (!this.audioConfig.audio || previousAudioTone !== audioAlertTone) {
      this.intializeAudio();
    }

    initFaviconSwitcher();
    this.clearRecurringTimer();
    this.playAudioEvery30Seconds();
  };

  shouldPlayAlert = () => {
    if (this.notificationConfig.playAlertOnlyWhenHidden) {
      return !WindowVisibilityHelper.isWindowVisible();
    }
    return true;
  };

  executeRecurringNotification = () => {
    if (this.store.hasUnreadConversation() && this.shouldPlayAlert()) {
      this.playAudioAlert();
      showBadgeOnFavicon();
    }
    this.resetRecurringTimer();
  };

  clearRecurringTimer = () => {
    if (this.recurringNotificationTimer) {
      clearTimeout(this.recurringNotificationTimer);
    }
  };

  resetRecurringTimer = () => {
    this.clearRecurringTimer();
    this.recurringNotificationTimer = setTimeout(
      this.executeRecurringNotification,
      NOTIFICATION_TIME
    );
  };

  playAudioEvery30Seconds = () => {
    const { audioAlertType, alertIfUnreadConversationExist } =
      this.notificationConfig;

    //  Audio alert is disabled dismiss the timer
    if (audioAlertType.includes('none')) return;

    // If unread conversation flag is disabled, dismiss the timer
    if (!alertIfUnreadConversationExist) return;

    this.resetRecurringTimer();
  };

  shouldNotifyOnMessage = message => {
    const { audioAlertType } = this.notificationConfig;
    if (audioAlertType.includes('none')) return false;
    if (audioAlertType.includes('all')) return true;

    const assignedToMe = isConversationAssignedToMe(
      message,
      this.currentUser.id
    );
    const isUnassigned = isConversationUnassigned(message);

    const shouldPlayAudio = [];

    if (
      audioAlertType.includes(EVENT_TYPES.ASSIGNED) ||
      audioAlertType.includes('mine')
    ) {
      shouldPlayAudio.push(assignedToMe);
    }
    if (audioAlertType.includes(EVENT_TYPES.UNASSIGNED)) {
      shouldPlayAudio.push(isUnassigned);
    }
    if (audioAlertType.includes(EVENT_TYPES.NOTME)) {
      shouldPlayAudio.push(!isUnassigned && !assignedToMe);
    }

    return shouldPlayAudio.some(Boolean);
  };

  triggerAlert = message => {
    if (!this.store.hasConversationPermission(this.currentUser)) return;
    if (!this.shouldNotifyOnMessage(message)) return;

    // Rings like a phone call, even with the dashboard on screen, unless the
    // agent already has this conversation open.
    if (this.notificationConfig.ringUntilOpened) {
      const isOpenOnScreen =
        WindowVisibilityHelper.isWindowVisible() &&
        this.store.isMessageFromCurrentConversation(message);
      if (isOpenOnScreen) return;
      this.startRinging(message.conversation_id);
      showBadgeOnFavicon();
      return;
    }

    if (WindowVisibilityHelper.isWindowVisible()) {
      if (this.store.isMessageFromCurrentConversation(message)) return;
      if (this.notificationConfig.playAlertOnlyWhenHidden) return;
    }

    this.playAudioAlert();
    showBadgeOnFavicon();
    this.playAudioEvery30Seconds();
  };

  onConversationBotHandoff = conversation => {
    // Adapt the handoff once to the existing audio-filter contract. Bot IDs
    // must not be treated as human assignees, even when their IDs overlap.
    this.triggerAlert({
      conversation_id: conversation.id,
      conversation: {
        assignee_id:
          conversation.meta?.assignee_type === 'User'
            ? conversation.meta.assignee?.id
            : null,
      },
    });
  };

  // TalkRobin: a new chat rings everyone who can see it (the website's agents
  // and admins), whoever it was auto-assigned to, like a phone line.
  onNewConversation = conversation => {
    if (!this.currentUser) return;
    if (this.notificationConfig.audioAlertType.includes('none')) return;
    if (!this.store.hasConversationPermission(this.currentUser)) return;
    if (conversation.status !== 'open') return; // bot still handling it

    if (this.notificationConfig.ringUntilOpened) {
      this.startRinging(conversation.id, { newChat: true });
    } else {
      this.playAudioAlert();
    }
    showBadgeOnFavicon();
  };

  // TalkRobin: a visitor landed on a website (sent only to agents who look
  // after it and haven't turned visitor alerts off). One sound, a short
  // message, and a buzz on phones; the phone notification comes as push.
  onVisitorArrived = ({ title, body } = {}) => {
    if (this.currentUser?.ui_settings?.visitor_arrival_alerts === false) return;
    const tone = new Audio(VISITOR_TONE_URL);
    tone.volume = 1;
    tone.play().catch(this.handlePlayError);
    navigator.vibrate?.([300, 150, 300]);
    useAlert([title, body].filter(Boolean).join(' — '), {
      duration: VISITOR_TOAST_DURATION,
    });
  };

  onNewMessage = message => {
    // Captain notes are context, not requests for attention. Only the dedicated
    // handoff event alerts agents, regardless of when its explanatory note arrives.
    if (message.private && message.sender?.type === 'captain_assistant') return;

    // If the conversation status is pending, then dismiss the alert
    // This case is common for all audio event types
    if (this.store.isMessageFromPendingConversation(message)) {
      return;
    }

    // If the message is sent by the current user then dismiss the alert
    if (isMessageFromCurrentUser(message, this.currentUser.id)) {
      return;
    }

    // If the message type is not incoming or private, then dismiss the alert
    const { message_type: messageType, private: isPrivate } = message;
    if (messageType !== MESSAGE_TYPE.INCOMING && !isPrivate) {
      return;
    }

    this.triggerAlert(message);
  };
}

export default new DashboardAudioNotificationHelper(GlobalStore);
